import { Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import tinycolor from "tinycolor2";
import * as XLSX from "xlsx";
import isEmpty from "lodash/isEmpty";
import { APP_ID, TimeOnlyZone, TimeZoneIndia } from "./constants";
const REG_ID = "reg_id";
export function toastError(error) {
  toast.error(getErrorResponse(error));
}
export function getErrorResponse(error) {
  if (typeof error === "string") {
    return error;
  }
  const statusCode = error?.response?.status || 500;
  let message;
  if (
    error?.response?.data?.message &&
    error.response?.status &&
    typeof error.response.data.message === "string"
  ) {
    message = error.response.data.message;
  } else if (error?.response?.data?.error?.message) {
    message = error.response.data.error.message;
  } else if (error?.message) {
    message = error.message;
  } else if (error?.request) {
    message = "No response received from the server";
  } else {
    message = "Some internal error occurred";
  }
  if (statusCode === 401 && !message.includes(" ")) {
    message = "Unauthorized request. Please check your credentials.";
  }
  return message;
}
export const getDbHeader = () => {
  return {
    headers: {
      "content-type": "application/json",
      dbauth: APP_ID, //db: testapp
    },
  };
};
export const saveUser = (role, data) => {
  localStorage.setItem(role, JSON.stringify(data));
};
export const getUser = (role) => {
  return JSON.parse(localStorage.getItem(role));
};
export const saveReg = (id) => {
  localStorage.setItem(REG_ID, id);
};
export const getReg = () => {
  return localStorage.getItem(REG_ID);
};
export const deleteUser = (role) => {
  localStorage.removeItem(role);
};
export const isUserLoggedIn = (role) => {
  return localStorage.getItem(role) !== null;
};
export function navToElementMarker(elem, msg) {
  if (elem.current) {
    elem.current.scrollIntoView({ behavior: "smooth", block: "center" });
    elem.current.parentElement.style.border = "1px solid red";
    setTimeout(() => {
      elem.current.parentElement.style.border = ""; // Revert to the original style
    }, 3000);
    toast.error(msg);
  }
}
export const UserAuth = ({ children, type }) => {
  return isUserLoggedIn(type) ? children : <Navigate to="/login" />;
};
export const formatDateMMMddYYYY = (dateString) => {
  const date = new Date(dateString);
  const options = { year: "numeric", month: "short", day: "numeric" };
  return date.toLocaleDateString("en-GB", options).replace(",", "");
};
export const getHeader = (type) => {
  return {
    headers: {
      "content-type": "application/json",
      token: getUser(type)?.token,
    },
  };
};
export const getMultipartHeader = (type) => {
  return {
    headers: {
      "Content-Type": "multipart/form-data",
      token: getUser(type)?.token,
    },
  };
};
export function useQuery() {
  return new URLSearchParams(useLocation().search);
}
export function getHoverColor(baseColor) {
  const hoverColor = tinycolor(baseColor).brighten(10).toString();
  return hoverColor;
}
export function getContrastingTextColor(hexColor) {
  const color = new tinycolor(hexColor);
  return color.isLight() ? "black" : "white";
}
export function hexToRGBA(hex, alpha = 1) {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
export function formatMongoDate(isoDateString) {
  const date = new Date(isoDateString);
  const day = date.getUTCDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getUTCMonth()];
  let hours = date.getUTCHours();
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 24-hour format to 12-hour format
  return `${day} ${month} ${hours}:${minutes}${ampm}`;
}
export function extractTime(isoString) {
  if (isEmpty(isoString)) {
    return "";
  }
  const dateObj = new Date(isoString);
  return dateObj.toLocaleTimeString("en-IN", TimeOnlyZone);
}
export function extractDate(isoString) {
  if (isEmpty(isoString)) {
    return "";
  }
  const dateObj = new Date(isoString);
  return dateObj.toLocaleDateString("en-IN", TimeZoneIndia); // en-GB format is dd/mm/yyyy
}
export function roundToTwoDecimals(num) {
  return parseFloat(num.toFixed(2));
}

export const exportToExcel = (data, fileName) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};
export const getContrastTextColor = (hexColor) => {
  // 1. SAFETY CHECK: Return a default color if hexColor is undefined, null, or not a string
  if (!hexColor || typeof hexColor !== "string") {
    return "#000000"; // Default to black text if no background color exists
  }

  // 2. Safely remove the hash
  const hex = hexColor.replace("#", "");

  // 3. Convert hex to RGB (handle 3-digit and 6-digit hex codes)
  const isShort = hex.length === 3;
  const rHex = isShort ? hex.charAt(0) + hex.charAt(0) : hex.substring(0, 2);
  const gHex = isShort ? hex.charAt(1) + hex.charAt(1) : hex.substring(2, 4);
  const bHex = isShort ? hex.charAt(2) + hex.charAt(2) : hex.substring(4, 6);

  const r = parseInt(rHex, 16);
  const g = parseInt(gHex, 16);
  const b = parseInt(bHex, 16);

  // 4. Calculate the YIQ brightness
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // 5. Return black for light backgrounds, white for dark backgrounds
  return yiq >= 128 ? "#000000" : "#FFFFFF";
};
// utils/util.js
export const gtag =
  typeof window !== "undefined"
    ? window.gtag ||
      function () {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(arguments);
      }
    : function () {
        console.log("GTag called server-side");
      };

export const getAuthHeader = (token) => {
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
};
